import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, addMonths, subMonths, addWeeks, subWeeks, isSameMonth, isToday, addDays, differenceInCalendarDays } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, List, LayoutGrid } from "lucide-react";

interface GanttItem {
  id: string;
  lead_id: string;
  title: string;
  notes: string | null;
  color: string;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  is_all_day: boolean;
  time_duration: number;
  start_position: number;
  width: number;
}

interface Lead {
  id: string;
  lead_name: string;
}

type CalendarViewMode = "month" | "week" | "agenda";

interface LeadCalendarViewProps {
  leads: Lead[];
  className?: string;
  onItemClick?: (leadId: string) => void;
  active?: boolean;
}

type EnrichedItem = GanttItem & { leadName: string };

const BASE_DAY_WIDTH = 80;

function getItemDates(item: GanttItem): { start: Date; end: Date } {
  if (item.start_date && item.end_date) {
    const [sy, sm, sd] = item.start_date.split("-").map(Number);
    const [ey, em, ed] = item.end_date.split("-").map(Number);
    const start = new Date(sy, sm - 1, sd);
    let end = new Date(ey, em - 1, ed);
    // If start and end are the same day, extend end by 1 day so the event is visible
    if (start.getTime() === end.getTime()) {
      end = addDays(end, 1);
    }
    return { start, end };
  }
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOffset = Math.floor(item.start_position / BASE_DAY_WIDTH);
  const durationDays = Math.max(1, Math.ceil(item.width / BASE_DAY_WIDTH));
  const start = addDays(today, startOffset);
  const end = addDays(start, durationDays);
  return { start, end };
}

function itemSpansDate(item: GanttItem, date: Date): boolean {
  const { start, end } = getItemDates(item);
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayEnd = addDays(dayStart, 1);
  return start < dayEnd && end > dayStart;
}

function isMultiDay(item: GanttItem): boolean {
  const { start, end } = getItemDates(item);
  return differenceInCalendarDays(end, start) > 1;
}

function getSpanningItemsForWeek(
  week: Date[],
  items: EnrichedItem[]
): { spanning: { item: EnrichedItem; startCol: number; endCol: number; lane: number }[]; laneCount: number } {
  const weekStart = week[0];
  const weekEndDate = addDays(week[6], 1);

  const multiDayItems = items
    .filter((i) => isMultiDay(i))
    .filter((i) => {
      const { start, end } = getItemDates(i);
      return start < weekEndDate && end > weekStart;
    })
    .sort((a, b) => getItemDates(a).start.getTime() - getItemDates(b).start.getTime());

  const lanes: number[] = []; // each lane stores the endCol of its last item
  const spanning: { item: EnrichedItem; startCol: number; endCol: number; lane: number }[] = [];

  for (const item of multiDayItems) {
    const { start, end } = getItemDates(item);
    const startCol = Math.max(0, differenceInCalendarDays(start, weekStart));
    const endCol = Math.min(7, differenceInCalendarDays(end, weekStart));

    let assignedLane = -1;
    for (let l = 0; l < lanes.length; l++) {
      if (lanes[l] <= startCol) {
        assignedLane = l;
        lanes[l] = endCol;
        break;
      }
    }
    if (assignedLane === -1) {
      assignedLane = lanes.length;
      lanes.push(endCol);
    }

    spanning.push({ item, startCol, endCol, lane: assignedLane });
  }

  return { spanning, laneCount: lanes.length };
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const LeadCalendarView = ({ leads, className, onItemClick, active = true }: LeadCalendarViewProps) => {
  const [viewMode, setViewMode] = useState<CalendarViewMode>("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [ganttItems, setGanttItems] = useState<GanttItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async (showLoader = true) => {
    if (leads.length === 0) {
      setGanttItems([]);
      setLoading(false);
      return;
    }

    if (showLoader) {
      setLoading(true);
    }

    try {
      const { data, error } = await supabase
        .from("lead_gantt_items")
        .select("id, lead_id, title, notes, color, start_date, end_date, start_time, is_all_day, time_duration, start_position, width")
        .in("lead_id", leads.map((l) => l.id));
      if (error) throw error;
      setGanttItems((data as GanttItem[]) || []);
    } catch (err) {
      console.error("Error fetching calendar items:", err);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, [leads]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Listen for realtime gantt item changes
  useEffect(() => {
    const handleGanttChange = () => fetchItems(false);
    window.addEventListener('gantt-items-changed', handleGanttChange);
    return () => window.removeEventListener('gantt-items-changed', handleGanttChange);
  }, [fetchItems]);

  // Fallback polling in case realtime drops or misses events
  useEffect(() => {
    if (!active) return;

    // Always refresh immediately when the popup becomes active
    void fetchItems(false);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchItems(false);
      }
    }, 30000); // 30s fallback — realtime handles instant updates

    return () => {
      window.clearInterval(intervalId);
    };
  }, [active, fetchItems]);

  const leadNameMap = useMemo(() => {
    const m = new Map<string, string>();
    leads.forEach((l) => m.set(l.id, l.lead_name));
    return m;
  }, [leads]);

  const allItemsWithNames = useMemo<EnrichedItem[]>(
    () => ganttItems.map((item) => ({ ...item, leadName: leadNameMap.get(item.lead_id) || "Lead" })),
    [ganttItems, leadNameMap]
  );

  const goBack = useCallback(() => {
    if (viewMode === "month") setCurrentDate((d) => subMonths(d, 1));
    else setCurrentDate((d) => subWeeks(d, 1));
  }, [viewMode]);

  const goForward = useCallback(() => {
    if (viewMode === "month") setCurrentDate((d) => addMonths(d, 1));
    else setCurrentDate((d) => addWeeks(d, 1));
  }, [viewMode]);

  const goToday = useCallback(() => setCurrentDate(new Date()), []);

  const title = useMemo(() => {
    if (viewMode === "month") return format(currentDate, "MMMM yyyy");
    if (viewMode === "week") {
      const ws = startOfWeek(currentDate, { weekStartsOn: 0 });
      const we = endOfWeek(currentDate, { weekStartsOn: 0 });
      return `${format(ws, "MMM d")} – ${format(we, "MMM d, yyyy")}`;
    }
    return format(currentDate, "MMMM yyyy");
  }, [currentDate, viewMode]);

  const monthDays = useMemo(() => {
    const ms = startOfMonth(currentDate);
    const me = endOfMonth(currentDate);
    const gridStart = startOfWeek(ms, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(me, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [currentDate]);

  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < monthDays.length; i += 7) {
      result.push(monthDays.slice(i, i + 7));
    }
    return result;
  }, [monthDays]);

  const weekDays = useMemo(() => {
    const ws = startOfWeek(currentDate, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: ws, end: addDays(ws, 6) });
  }, [currentDate]);

  const agendaItems = useMemo(() => {
    const today = new Date();
    const days: { date: Date; items: EnrichedItem[] }[] = [];
    for (let i = 0; i < 14; i++) {
      const d = addDays(today, i);
      const dayItems = allItemsWithNames
        .filter((item) => itemSpansDate(item, d))
        .sort((a, b) => {
          if (a.is_all_day && !b.is_all_day) return -1;
          if (!a.is_all_day && b.is_all_day) return 1;
          const timeA = a.start_time || "99:99";
          const timeB = b.start_time || "99:99";
          return timeA.localeCompare(timeB);
        });
      if (dayItems.length > 0) {
        days.push({ date: d, items: dayItems });
      }
    }
    return days;
  }, [allItemsWithNames]);

  const getItemsForDay = useCallback(
    (day: Date) =>
      allItemsWithNames
        .filter((item) => itemSpansDate(item, day))
        .sort((a, b) => {
          // All-day events first
          if (a.is_all_day && !b.is_all_day) return -1;
          if (!a.is_all_day && b.is_all_day) return 1;
          // Then sort by start_time ascending (earlier on top)
          const timeA = a.start_time || "99:99";
          const timeB = b.start_time || "99:99";
          return timeA.localeCompare(timeB);
        }),
    [allItemsWithNames]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className={cn("border rounded-lg bg-card overflow-hidden", className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 sm:p-3 border-b bg-muted/30 gap-1.5 sm:gap-0">
        <div className="flex items-center gap-0.5 sm:gap-1 w-full sm:w-auto">
          <Button variant="ghost" size="icon" className="h-6 w-6 sm:h-7 sm:w-7 shrink-0" onClick={goBack}>
            <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-[10px] sm:text-xs h-6 sm:h-7 px-1.5 sm:px-2 shrink-0" onClick={goToday}>
            Today
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 sm:h-7 sm:w-7 shrink-0" onClick={goForward}>
            <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Button>
          <span className="text-[11px] sm:text-sm font-semibold ml-1 truncate">{title}</span>
        </div>
        <div className="flex items-center bg-muted rounded-md p-0.5 w-full sm:w-auto">
          <Button
            variant={viewMode === "month" ? "secondary" : "ghost"}
            size="sm"
            className="h-6 text-[10px] sm:text-[11px] px-1.5 sm:px-2 gap-0.5 flex-1 sm:flex-none"
            onClick={() => setViewMode("month")}
          >
            <LayoutGrid className="h-3 w-3" />
            Month
          </Button>
          <Button
            variant={viewMode === "week" ? "secondary" : "ghost"}
            size="sm"
            className="h-6 text-[10px] sm:text-[11px] px-1.5 sm:px-2 gap-0.5 flex-1 sm:flex-none"
            onClick={() => setViewMode("week")}
          >
            <CalendarIcon className="h-3 w-3" />
            Week
          </Button>
          <Button
            variant={viewMode === "agenda" ? "secondary" : "ghost"}
            size="sm"
            className="h-6 text-[10px] sm:text-[11px] px-1.5 sm:px-2 gap-0.5 flex-1 sm:flex-none"
            onClick={() => setViewMode("agenda")}
          >
            <List className="h-3 w-3" />
            Agenda
          </Button>
        </div>
      </div>

      {/* Month View */}
      {viewMode === "month" && (
        <div className="p-1 sm:p-2 overflow-auto max-h-[60vh] sm:max-h-none">
          {/* Day headers */}
          <div className="grid grid-cols-7 mb-0.5 sm:mb-1">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
              <div key={i} className="text-center text-[9px] sm:text-[10px] font-medium text-muted-foreground py-0.5 sm:py-1">
                <span className="sm:hidden">{d}</span>
                <span className="hidden sm:inline">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][i]}</span>
              </div>
            ))}
          </div>
          {/* Week rows */}
          <div className="bg-border rounded-md overflow-hidden">
            {weeks.map((week, weekIdx) => {
              const { spanning, laneCount } = getSpanningItemsForWeek(week, allItemsWithNames);
              const spanningHeight = laneCount * 16;
              return (
                <div key={weekIdx} className="relative">
                  {/* Day cells */}
                  <div className="grid grid-cols-7 gap-px">
                    {week.map((day) => {
                      const singleDayItems = getItemsForDay(day).filter((i) => !isMultiDay(i));
                      const inMonth = isSameMonth(day, currentDate);
                      return (
                        <div
                          key={day.toISOString()}
                          className={cn(
                            "min-h-[48px] sm:min-h-[80px] bg-card p-0.5 sm:p-1 transition-colors",
                            !inMonth && "bg-muted/30",
                            isToday(day) && "ring-1 ring-primary ring-inset"
                          )}
                        >
                          <div
                            className={cn(
                              "text-[10px] sm:text-[11px] font-medium mb-0.5",
                              isToday(day) ? "text-primary font-bold" : !inMonth ? "text-muted-foreground/50" : "text-foreground"
                            )}
                          >
                            {format(day, "d")}
                          </div>
                          {/* Spacer for spanning events */}
                          {spanningHeight > 0 && <div style={{ height: spanningHeight }} />}
                          {/* Single-day events */}
                          <div className="space-y-0.5">
                            {singleDayItems.slice(0, 2).map((item) => (
                              <div
                                key={item.id}
                                className="text-[8px] sm:text-[9px] leading-tight truncate rounded px-0.5 sm:px-1 py-px sm:py-0.5 text-white font-medium cursor-pointer hover:brightness-110 transition-all"
                                style={{ backgroundColor: item.color }}
                                title={`${item.leadName}: ${item.title}${item.start_time && !item.is_all_day ? ` @ ${item.start_time}` : ""}`}
                                onClick={() => onItemClick?.(item.lead_id)}
                              >
                                <span className="sm:hidden">{item.title}</span>
                                <span className="hidden sm:inline">
                                  {!item.is_all_day && item.start_time && (
                                    <span className="opacity-80 mr-0.5">{item.start_time}</span>
                                  )}
                                  {item.leadName}: {item.title}
                                </span>
                              </div>
                            ))}
                            {singleDayItems.length > 2 && (
                              <div className="text-[8px] sm:text-[9px] text-muted-foreground pl-0.5 sm:pl-1">+{singleDayItems.length - 2} more</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Multi-day spanning bars */}
                  {spanning.map(({ item, startCol, endCol, lane }) => (
                    <div
                      key={`${item.id}-w${weekIdx}`}
                      className="absolute text-[8px] sm:text-[9px] leading-tight truncate rounded px-0.5 sm:px-1 py-px sm:py-0.5 text-white font-medium cursor-pointer hover:brightness-110 transition-all z-10"
                      style={{
                        backgroundColor: item.color,
                        left: `${(startCol / 7) * 100}%`,
                        width: `${((endCol - startCol) / 7) * 100}%`,
                        top: `${18 + lane * 16}px`,
                        height: 14,
                      }}
                      title={`${item.leadName}: ${item.title}`}
                      onClick={() => onItemClick?.(item.lead_id)}
                    >
                      <span className="sm:hidden">{item.title}</span>
                      <span className="hidden sm:inline">{item.leadName}: {item.title}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week View — compact vertical day list for popup */}
      {viewMode === "week" && (
        <div className="overflow-auto max-h-[60vh] sm:max-h-[420px] p-1.5 sm:p-2 space-y-1">
          {weekDays.map((day) => {
            const dayItems = getItemsForDay(day);
            const hasItems = dayItems.length > 0;
            return (
              <div
                key={day.toISOString()}
                className={cn(
                  "rounded-lg border transition-colors",
                  isToday(day) ? "border-primary/40 bg-primary/5" : "border-transparent bg-muted/30"
                )}
              >
                {/* Day header */}
                <div className={cn(
                  "flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5",
                  !hasItems && "pb-1 sm:pb-1.5"
                )}>
                  <div className={cn(
                    "flex items-center justify-center h-6 w-6 sm:h-7 sm:w-7 rounded-full text-[11px] sm:text-xs font-bold shrink-0",
                    isToday(day) ? "bg-primary text-primary-foreground" : "text-foreground"
                  )}>
                    {format(day, "d")}
                  </div>
                  <span className={cn(
                    "text-[11px] sm:text-xs font-medium",
                    isToday(day) ? "text-primary" : "text-muted-foreground"
                  )}>
                    <span className="sm:hidden">{format(day, "EEE")}</span>
                    <span className="hidden sm:inline">{format(day, "EEEE")}</span>
                  </span>
                  {!hasItems && (
                    <span className="text-[9px] sm:text-[10px] text-muted-foreground/50 ml-auto">No events</span>
                  )}
                </div>
                {/* Events */}
                {hasItems && (
                  <div className="px-2 sm:px-3 pb-1.5 sm:pb-2 space-y-1">
                    {dayItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-1.5 sm:gap-2 rounded-md px-1.5 sm:px-2 py-0.5 sm:py-1 cursor-pointer hover:brightness-95 transition-all text-white text-[10px] sm:text-[11px] font-medium"
                        style={{ backgroundColor: item.color }}
                        onClick={() => onItemClick?.(item.lead_id)}
                        title={`${item.leadName}: ${item.title}${item.start_time && !item.is_all_day ? ` @ ${item.start_time}` : ""}`}
                      >
                        <div className="min-w-0 flex-1 truncate">
                          {!item.is_all_day && item.start_time && (
                            <span className="opacity-80 mr-1">{item.start_time}</span>
                          )}
                          {item.leadName}: {item.title}
                        </div>
                        {item.is_all_day && (
                          <span className="text-[8px] sm:text-[9px] opacity-70 shrink-0">All day</span>
                        )}
                        {!item.is_all_day && item.time_duration > 0 && (
                          <span className="text-[8px] sm:text-[9px] opacity-70 shrink-0">{item.time_duration}m</span>
                        )}
                        {isMultiDay(item) && (
                          <span className="text-[8px] sm:text-[9px] bg-white/20 rounded px-1 shrink-0 hidden sm:inline">Multi-day</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Agenda View */}
      {viewMode === "agenda" && (
        <div className="p-2 sm:p-3 space-y-2 max-h-[60vh] sm:max-h-[600px] overflow-auto">
          {agendaItems.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-muted-foreground text-xs sm:text-sm">
              No tasks scheduled in the next 14 days
            </div>
          ) : (
            agendaItems.map(({ date, items }) => (
              <div key={date.toISOString()}>
                <div className={cn("text-[11px] sm:text-xs font-semibold mb-1", isToday(date) ? "text-primary" : "text-foreground")}>
                  {isToday(date) ? "Today" : format(date, "EEE, MMM d")}
                </div>
                <div className="space-y-1 ml-1.5 sm:ml-2 mb-2 sm:mb-3">
                  {items.map((item) => (
                    <div key={item.id} className="flex items-start gap-1.5 sm:gap-2 group cursor-pointer hover:bg-muted/50 rounded-md px-1 py-0.5 -mx-1 transition-colors" onClick={() => onItemClick?.(item.lead_id)}>
                      <div className="w-2 h-2 rounded-full mt-1 shrink-0" style={{ backgroundColor: item.color }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] sm:text-xs font-medium truncate">{item.leadName}: {item.title}</div>
                        <div className="text-[9px] sm:text-[10px] text-muted-foreground flex gap-1.5 sm:gap-2 flex-wrap">
                          {!item.is_all_day && item.start_time && (
                            <span>{item.start_time} · {item.time_duration}min</span>
                          )}
                          {item.is_all_day && <span>All day</span>}
                          {isMultiDay(item) && <span>Multi-day</span>}
                        </div>
                        {item.notes && (
                          <div className="text-[9px] sm:text-[10px] text-muted-foreground/70 truncate">{item.notes}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default LeadCalendarView;
